import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, FileText, Image, FolderOpen, Users, 
  LogOut, Menu, X, ChevronRight, Shield, Loader2,
  Bell, Search, Home, BarChart3, Plus, Upload, Trash2,
  Edit2, Eye, Download, Check, XCircle, RefreshCw,
  GraduationCap, UserCog, Clock, Activity, Lock, 
  TrendingUp, Zap, Globe, Settings, Filter, Replace,
  Link2, Calendar, AlertCircle, CheckCircle2, History,
  RotateCcw, ArrowUp, ArrowDown, GripVertical, Award,
  ExternalLink, Ban, RotateCw, FileDown, QrCode, BookOpen, Inbox
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import ApplicationsInbox from '@/components/admin/ApplicationsInbox';
import { OFFICIAL_SEAL } from '@/lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar navigation items
const navItems = [
  { id: 'dashboard', label: 'Command Center', icon: LayoutDashboard, description: 'Executive overview' },
  { id: 'applications', label: 'Applications', icon: Inbox, description: 'Admissions inbox' },
  { id: 'content', label: 'Content Management', icon: FileText, description: 'Edit website content' },
  { id: 'scfr', label: 'SCFR-Journal', icon: BookOpen, description: 'Publication management', external: '/internal/scfr-admin' },
  { id: 'media', label: 'Media Library', icon: Image, description: 'Images and videos' },
  { id: 'documents', label: 'Documents', icon: FolderOpen, description: 'Files and downloads' },
  { id: 'certificates', label: 'Certificates', icon: Award, description: 'Issue and manage' },
  { id: 'users', label: 'User Management', icon: Users, description: 'Manage accounts' },
];

// ============================================
// PHASE 2: DASHBOARD HOME - COMMAND CENTER
// ============================================
function DashboardHome({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, activityRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/cms/dashboard/stats`),
        fetch(`${API_URL}/api/admin/cms/dashboard/activity`)
      ]);
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      if (activityRes.ok) {
        const activityData = await activityRes.json();
        setActivity(activityData.activities || []);
      }
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      toast.error('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      {/* Header with Status */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            Command Center
          </h2>
          <p className="text-slate-400 mt-2">Real-time platform intelligence and control</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-slate-500">Last updated</p>
            <p className="text-sm text-slate-400">{lastRefresh.toLocaleTimeString()}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchDashboardData}
            disabled={loading}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* System Status Bar */}
      <div className="bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent border border-emerald-500/20 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-emerald-400 font-medium">All Systems Operational</span>
          <span className="text-slate-500 text-sm ml-auto hidden sm:block">
            CMS, Auth, Database Connected
          </span>
        </div>
      </div>
      
      {/* Primary Metrics - User Analytics */}
      <div>
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Users className="w-4 h-4" />
          User Analytics
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'from-blue-500 to-blue-600', trend: '+12%' },
            { label: 'Students', value: stats?.students || 0, icon: GraduationCap, color: 'from-emerald-500 to-emerald-600', trend: '+8%' },
            { label: 'Instructors', value: stats?.instructors || 0, icon: UserCog, color: 'from-purple-500 to-purple-600', trend: '+3%' },
            { label: 'Administrators', value: stats?.admins || 0, icon: Shield, color: 'from-amber-500 to-amber-600', trend: '—' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs text-emerald-400 font-medium">{stat.trend}</span>
                  </div>
                  <div className="mt-4">
                    <p className="text-3xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Secondary Metrics - Content & Resources */}
      <div>
        <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Content & Resources
        </h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Content Blocks', value: stats?.content_items || 0, icon: FileText, color: 'bg-indigo-500', action: 'content' },
            { label: 'Media Files', value: stats?.media_files || 0, icon: Image, color: 'bg-pink-500', action: 'media' },
            { label: 'Documents', value: stats?.documents || 0, icon: FolderOpen, color: 'bg-cyan-500', action: 'documents' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1 }}
            >
              <Card 
                className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                onClick={() => onNavigate(stat.action)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-slate-400">{stat.label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Quick Actions & Activity Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'Edit Home Page', icon: Home, section: 'content', color: 'text-blue-400' },
              { label: 'Upload Media', icon: Upload, section: 'media', color: 'text-pink-400' },
              { label: 'Add Document', icon: Plus, section: 'documents', color: 'text-cyan-400' },
              { label: 'Manage Users', icon: Users, section: 'users', color: 'text-purple-400' },
            ].map((action, i) => (
              <button
                key={i}
                onClick={() => onNavigate(action.section)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
                data-testid={`quick-action-${action.section}`}
              >
                <action.icon className={`w-5 h-5 ${action.color}`} />
                <span className="text-sm text-slate-300">{action.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-500 ml-auto" />
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Recent Logins */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-400" />
              Recent Logins
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_logins?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_logins.slice(0, 5).map((login, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-700/20">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      login.role === 'admin' ? 'bg-amber-500/20 text-amber-400' :
                      login.role === 'instructor' ? 'bg-purple-500/20 text-purple-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {login.email?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{login.email}</p>
                      <p className="text-xs text-slate-500 capitalize">{login.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent logins</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              Platform Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length > 0 ? (
              <div className="space-y-2">
                {activity.slice(0, 5).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-slate-700/20">
                    <div className={`w-2 h-2 mt-2 rounded-full flex-shrink-0 ${
                      item.type === 'survey' ? 'bg-emerald-400' :
                      item.type === 'contact' ? 'bg-blue-400' :
                      'bg-purple-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{item.description}</p>
                      <p className="text-xs text-slate-500">
                        {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No recent activity</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CMS Pages Status */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-400" />
            CMS-Enabled Pages
          </CardTitle>
          <CardDescription className="text-slate-400">
            Pages reading content dynamically from the CMS
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
            {['home', 'programs', 'research', 'about', 'admissions', 'wait-theory', 'contact'].map((page, i) => (
              <div key={page} className="flex items-center gap-2 p-3 rounded-lg bg-slate-700/30">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-slate-300 capitalize">{page.replace('-', ' ')}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Content Versions */}
      <RecentContentVersions onNavigate={onNavigate} />
    </div>
  );
}

// Recent Content Versions Component
function RecentContentVersions({ onNavigate }) {
  const [recentVersions, setRecentVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentVersions();
  }, []);

  const fetchRecentVersions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/versions/recent?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setRecentVersions(data.versions || []);
      }
    } catch (err) {
      console.error('Failed to fetch recent versions:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="recent-versions-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <History className="w-5 h-5 text-blue-400" />
            Recent Content Changes
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onNavigate('content')}
            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <CardDescription className="text-slate-400">
          Version history across all CMS-managed pages
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
          </div>
        ) : recentVersions.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-10 h-10 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500 text-sm">No content changes yet</p>
            <p className="text-xs text-slate-600 mt-1">Edit content to see version history</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentVersions.slice(0, 6).map((version, i) => (
              <div 
                key={version.id || i} 
                className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/20 hover:bg-slate-700/30 transition-colors"
                data-testid={`recent-version-${i}`}
              >
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white capitalize truncate">
                      {version.page?.replace('-', ' ') || 'Unknown'}
                    </span>
                    <span className="text-slate-600">/</span>
                    <span className="text-sm text-slate-400 capitalize truncate">
                      {version.section?.replace(/_/g, ' ') || 'section'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3 text-slate-500" />
                    <span className="text-xs text-slate-500">
                      {version.version_created_at ? formatTimeAgo(version.version_created_at) : 'Unknown'}
                    </span>
                    {version.modified_by && (
                      <>
                        <span className="text-slate-600">•</span>
                        <span className="text-xs text-slate-500 truncate">{version.modified_by}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-400">
                    Updated
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// PHASE 2: CONTENT MANAGEMENT - STRUCTURED FORMS
// ============================================
function ContentManagement() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showVersions, setShowVersions] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Page configurations with CMS status
  const PAGE_CONFIG = {
    home: { name: 'Home Page', status: 'active', icon: Home },
    programs: { name: 'Programs', status: 'active', icon: GraduationCap },
    research: { name: 'Research', status: 'active', icon: BarChart3 },
    about: { name: 'About Us', status: 'active', icon: Users },
    admissions: { name: 'Admissions', status: 'active', icon: FileText },
    'wait-theory': { name: 'WAIT Theory', status: 'active', icon: Zap },
    contact: { name: 'Contact', status: 'active', icon: Globe },
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/pages`);
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
      }
    } catch (err) {
      toast.error('Failed to fetch pages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPageContent = async (pageName) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/page/${pageName}`);
      if (res.ok) {
        const data = await res.json();
        setPageContent(data.blocks || []);
        setSelectedPage(pageName);
      }
    } catch (err) {
      toast.error('Failed to fetch page content');
    } finally {
      setLoading(false);
    }
  };

  const seedPageContent = async (pageName) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/page/${pageName}/seed`, {
        method: 'POST'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Seeded ${data.block_count} content blocks`);
        fetchPageContent(pageName);
      } else {
        toast.info(data.message || 'Page already has content');
      }
    } catch (err) {
      toast.error('Failed to seed content');
    }
  };

  const updateBlock = async (blockId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/block/${blockId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.version_saved ? 'Content updated (version saved)' : 'Content updated');
        fetchPageContent(selectedPage);
        setEditingBlock(null);
      } else {
        toast.error('Failed to update content');
      }
    } catch (err) {
      toast.error('Failed to update content');
    }
  };

  // Fetch version history for a block
  const fetchVersions = async (blockId) => {
    setLoadingVersions(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/block/${blockId}/versions`);
      if (res.ok) {
        const data = await res.json();
        setVersions(data.versions || []);
        setShowVersions(blockId);
      }
    } catch (err) {
      toast.error('Failed to fetch version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Restore a previous version
  const restoreVersion = async (blockId, versionId) => {
    if (!confirm('Restore this version? Current content will be saved as a new version.')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/block/${blockId}/restore/${versionId}`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Version restored successfully');
        fetchPageContent(selectedPage);
        setShowVersions(null);
        setVersions([]);
      } else {
        toast.error('Failed to restore version');
      }
    } catch (err) {
      toast.error('Failed to restore version');
    }
  };

  // Move block up/down (ordering)
  const moveBlock = async (blockId, direction) => {
    const currentIndex = pageContent.findIndex(b => b.id === blockId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= pageContent.length) return;

    // Create new order array
    const newContent = [...pageContent];
    const [removed] = newContent.splice(currentIndex, 1);
    newContent.splice(newIndex, 0, removed);

    // Build reorder request
    const blockOrders = newContent.map((block, idx) => ({
      id: block.id,
      order: idx + 1
    }));

    try {
      const res = await fetch(`${API_URL}/api/admin/cms/content/page/${selectedPage}/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blockOrders)
      });
      if (res.ok) {
        toast.success('Block order updated');
        fetchPageContent(selectedPage);
      } else {
        toast.error('Failed to reorder blocks');
      }
    } catch (err) {
      toast.error('Failed to reorder blocks');
    }
  };

  if (loading && !selectedPage) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-indigo-400" />
            Content Management
          </h2>
          <p className="text-slate-400 mt-1">Edit page content with structured forms</p>
        </div>
        {selectedPage && (
          <Button 
            variant="outline"
            onClick={() => { setSelectedPage(null); setEditingBlock(null); }}
            className="border-slate-600 text-slate-300"
          >
            <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
            Back to Pages
          </Button>
        )}
      </div>

      {!selectedPage ? (
        // Page Selection Grid
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(PAGE_CONFIG).map(([pageKey, config]) => {
            const pageData = pages.find(p => p.page === pageKey);
            const hasContent = pageData?.has_content || false;
            const blockCount = pageData?.block_count || 0;
            
            return (
              <Card 
                key={pageKey} 
                className="bg-slate-800/50 border-slate-700 hover:border-purple-500/50 cursor-pointer transition-all hover:shadow-lg hover:shadow-purple-500/10"
                onClick={() => fetchPageContent(pageKey)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      hasContent ? 'bg-emerald-500/20' : 'bg-slate-700'
                    }`}>
                      <config.icon className={`w-6 h-6 ${hasContent ? 'text-emerald-400' : 'text-slate-500'}`} />
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      hasContent ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
                    }`}>
                      {hasContent ? 'Active' : 'Empty'}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{config.name}</h3>
                  <p className="text-sm text-slate-400">
                    {blockCount} content block{blockCount !== 1 ? 's' : ''}
                  </p>
                  {!hasContent && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="mt-4 w-full border-slate-600 text-slate-300"
                      onClick={(e) => {
                        e.stopPropagation();
                        seedPageContent(pageKey);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Initialize Content
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        // Page Content Editor
        <div className="space-y-4">
          {/* Page Header */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                {PAGE_CONFIG[selectedPage]?.icon && 
                  <span className="text-indigo-400">
                    {(() => { const Icon = PAGE_CONFIG[selectedPage].icon; return <Icon className="w-5 h-5" />; })()}
                  </span>
                }
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {PAGE_CONFIG[selectedPage]?.name || selectedPage}
                </h3>
                <p className="text-sm text-slate-400">{pageContent.length} content blocks</p>
              </div>
            </div>
          </div>

          {pageContent.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-400 mb-2">No Content Yet</h3>
                <p className="text-slate-500 mb-4">Initialize content blocks to get started</p>
                <Button onClick={() => seedPageContent(selectedPage)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Initialize Content
                </Button>
              </CardContent>
            </Card>
          ) : (
            pageContent.map((block, index) => (
              <Card key={block.id} className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Reorder Buttons */}
                      <div className="flex flex-col gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveBlock(block.id, 'up')}
                          disabled={index === 0}
                          className={`h-5 w-5 p-0 ${index === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          data-testid={`move-up-${block.id}`}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveBlock(block.id, 'down')}
                          disabled={index === pageContent.length - 1}
                          className={`h-5 w-5 p-0 ${index === pageContent.length - 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          data-testid={`move-down-${block.id}`}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-slate-400" />
                      </div>
                      <div>
                        <CardTitle className="text-white text-base capitalize">
                          {block.section.replace(/_/g, ' ')}
                        </CardTitle>
                        <p className="text-xs text-slate-500">{block.content_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        block.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600 text-slate-400'
                      }`}>
                        {block.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {/* Version History Button */}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => fetchVersions(block.id)}
                        className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                        data-testid={`version-history-${block.id}`}
                      >
                        <History className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => setEditingBlock(editingBlock === block.id ? null : block.id)}
                        className="text-slate-400 hover:text-white"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editingBlock === block.id ? (
                    <StructuredBlockEditor 
                      block={block} 
                      onSave={(updates) => updateBlock(block.id, updates)}
                      onCancel={() => setEditingBlock(null)}
                    />
                  ) : (
                    <ContentPreview content={block.content} />
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Version History Modal */}
      {showVersions && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-2xl max-h-[80vh] flex flex-col" data-testid="version-history-modal">
            <CardHeader className="border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" />
                  Version History
                </CardTitle>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => { setShowVersions(null); setVersions([]); }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-slate-400 mt-1">
                View and restore previous versions of this content block
              </p>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-auto">
              {loadingVersions ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                </div>
              ) : versions.length === 0 ? (
                <div className="p-8 text-center">
                  <History className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400">No version history available</p>
                  <p className="text-sm text-slate-500 mt-1">Versions are created when content is edited</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {versions.map((version, idx) => (
                    <div 
                      key={version.id} 
                      className="p-4 hover:bg-slate-700/30 transition-colors"
                      data-testid={`version-item-${version.id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          {/* Version Header */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              idx === 0 ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-600 text-slate-400'
                            }`}>
                              {idx === 0 ? 'Most Recent' : `Version ${versions.length - idx}`}
                            </span>
                            {version.change_summary && (
                              <span className="text-xs text-slate-500 truncate">{version.change_summary}</span>
                            )}
                          </div>
                          
                          {/* Version Details */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <div className="flex items-center gap-2 text-slate-400">
                              <Clock className="w-3 h-3" />
                              <span>
                                {version.version_created_at 
                                  ? new Date(version.version_created_at).toLocaleString()
                                  : 'Unknown date'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-400">
                              <FileText className="w-3 h-3" />
                              <span className="capitalize">{version.page} / {version.section?.replace(/_/g, ' ')}</span>
                            </div>
                            {version.modified_by && (
                              <div className="flex items-center gap-2 text-slate-400">
                                <Users className="w-3 h-3" />
                                <span>{version.modified_by}</span>
                              </div>
                            )}
                          </div>

                          {/* Content Preview */}
                          {version.content && (
                            <div className="mt-3 p-2 bg-slate-900/50 rounded border border-slate-700 text-xs text-slate-500 max-h-20 overflow-hidden">
                              {Object.entries(version.content).slice(0, 3).map(([key, val]) => (
                                <div key={key} className="truncate">
                                  <span className="text-slate-400">{key}:</span>{' '}
                                  {typeof val === 'string' ? val.substring(0, 60) + (val.length > 60 ? '...' : '') : JSON.stringify(val).substring(0, 40)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Restore Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restoreVersion(showVersions, version.id)}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:text-blue-300 flex-shrink-0"
                          data-testid={`restore-version-${version.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="p-4 border-t border-slate-700 bg-slate-900/50 flex-shrink-0">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <AlertCircle className="w-4 h-4" />
                <span>Restoring a version will save the current content as a new version before replacing it</span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Structured Block Editor with Form Fields
function StructuredBlockEditor({ block, onSave, onCancel }) {
  const [formData, setFormData] = useState(block.content);
  const [isActive, setIsActive] = useState(block.is_active);

  // Define protected (media) fields
  const isProtectedField = (key) => {
    return key.includes('video') || key.includes('image') || key.includes('poster') || key.includes('url') && !key.includes('link');
  };

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateNestedField = (parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: { ...prev[parentKey], [childKey]: value }
    }));
  };

  const handleSave = () => {
    onSave({ content: formData, is_active: isActive });
  };

  // Render a single form field
  const renderField = (key, value, parentKey = null) => {
    if (typeof value === 'object' && value !== null) return null;
    if (Array.isArray(value)) return null;

    const isProtected = isProtectedField(key);
    const fieldId = parentKey ? `${parentKey}-${key}` : key;
    const isLongText = typeof value === 'string' && value.length > 100;

    return (
      <div key={fieldId} className="space-y-2">
        <Label className="text-slate-300 capitalize flex items-center gap-2 text-sm">
          {key.replace(/_/g, ' ')}
          {isProtected && (
            <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded">
              <Lock className="w-3 h-3" />
              Locked
            </span>
          )}
        </Label>
        {isProtected ? (
          <Input value={value || ''} disabled className="bg-slate-900/50 border-slate-700 text-slate-500 cursor-not-allowed" />
        ) : isLongText || key === 'description' ? (
          <Textarea
            value={value || ''}
            onChange={(e) => parentKey ? updateNestedField(parentKey, key, e.target.value) : updateField(key, e.target.value)}
            className="bg-slate-900 border-slate-700 text-white min-h-[100px]"
          />
        ) : (
          <Input
            value={value || ''}
            onChange={(e) => parentKey ? updateNestedField(parentKey, key, e.target.value) : updateField(key, e.target.value)}
            className="bg-slate-900 border-slate-700 text-white"
          />
        )}
      </div>
    );
  };

  // Render CTA button fields
  const renderCTAField = (key, ctaData) => {
    if (!ctaData || typeof ctaData !== 'object') return null;
    
    return (
      <div key={key} className="p-4 bg-slate-900/30 rounded-lg border border-slate-700 space-y-3">
        <Label className="text-slate-300 capitalize font-semibold text-sm">
          {key.replace(/_/g, ' ')}
        </Label>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Button Text</Label>
            <Input
              value={ctaData.text || ''}
              onChange={(e) => updateNestedField(key, 'text', e.target.value)}
              className="bg-slate-900 border-slate-700 text-white"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Link URL</Label>
            <Input
              value={ctaData.link || ''}
              onChange={(e) => updateNestedField(key, 'link', e.target.value)}
              className="bg-slate-900 border-slate-700 text-white"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Protection Notice */}
      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-400 font-medium text-sm">Visual Protection Active</p>
          <p className="text-amber-400/70 text-xs mt-1">Text fields are editable. Media URLs are locked to preserve visuals.</p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="grid gap-4">
        {Object.entries(formData).map(([key, value]) => {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            if (key.includes('cta')) return renderCTAField(key, value);
            return null;
          }
          if (Array.isArray(value)) return null;
          return renderField(key, value);
        })}
      </div>

      {/* Array Fields (stats, items) */}
      {Object.entries(formData).map(([key, value]) => {
        if (!Array.isArray(value)) return null;
        return (
          <div key={key} className="p-4 bg-slate-900/30 rounded-lg border border-slate-700 space-y-3">
            <Label className="text-slate-300 capitalize font-semibold text-sm">
              {key.replace(/_/g, ' ')} ({value.length} items)
            </Label>
            {value.map((item, idx) => (
              <div key={idx} className="grid md:grid-cols-2 gap-2 p-3 bg-slate-800/50 rounded">
                {Object.entries(item).map(([itemKey, itemValue]) => (
                  <div key={itemKey}>
                    <Label className="text-xs text-slate-500 capitalize mb-1 block">{itemKey}</Label>
                    <Input
                      value={itemValue || ''}
                      onChange={(e) => {
                        const newItems = [...value];
                        newItems[idx] = { ...newItems[idx], [itemKey]: e.target.value };
                        updateField(key, newItems);
                      }}
                      className="bg-slate-900 border-slate-700 text-white text-sm"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
        <label className="flex items-center gap-2 text-slate-400 text-sm">
          <input 
            type="checkbox" 
            checked={isActive} 
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-slate-600"
          />
          Active on live site
        </label>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel} className="text-slate-400">Cancel</Button>
          <Button onClick={handleSave} className="bg-purple-600 hover:bg-purple-700">
            <Check className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}

// Content Preview Component
function ContentPreview({ content }) {
  if (!content) return null;
  
  return (
    <div className="p-4 bg-slate-900/30 rounded-lg space-y-2">
      {content.badge && <span className="inline-block px-2 py-1 bg-slate-700 rounded text-xs text-slate-300">{content.badge}</span>}
      {content.title && <h4 className="text-white font-semibold">{content.title}</h4>}
      {content.subtitle && <p className="text-slate-400 text-sm">{content.subtitle}</p>}
      {content.description && <p className="text-slate-500 text-sm line-clamp-2">{content.description}</p>}
      {(content.cta_primary || content.cta_secondary || content.button_text) && (
        <div className="flex gap-2 mt-2">
          {content.cta_primary && <span className="px-3 py-1 bg-purple-600 rounded text-xs text-white">{content.cta_primary.text}</span>}
          {content.cta_secondary && <span className="px-3 py-1 border border-slate-600 rounded text-xs text-slate-300">{content.cta_secondary.text}</span>}
          {content.button_text && <span className="px-3 py-1 bg-purple-600 rounded text-xs text-white">{content.button_text}</span>}
        </div>
      )}
    </div>
  );
}

// ============================================
// PHASE 2: MEDIA LIBRARY - IMPROVED UI
// ============================================
function MediaLibrary() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    fetchMedia();
  }, [filter]);

  const fetchMedia = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/admin/cms/media`
        : `${API_URL}/api/admin/cms/media?media_type=${filter}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        let mediaList = data.media || [];
        
        // Apply sorting
        if (sortBy === 'newest') {
          mediaList.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } else if (sortBy === 'oldest') {
          mediaList.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        } else if (sortBy === 'name') {
          mediaList.sort((a, b) => a.title.localeCompare(b.title));
        }
        
        setMedia(mediaList);
      }
    } catch (err) {
      toast.error('Failed to fetch media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      const res = await fetch(`${API_URL}/api/admin/cms/media/upload`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        toast.success('Media uploaded successfully');
        fetchMedia();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const deleteMedia = async (mediaId) => {
    if (!confirm('Delete this media file?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/media/${mediaId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Media deleted');
        fetchMedia();
        setSelectedMedia(null);
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="media-library">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Image className="w-7 h-7 text-pink-400" />
            Media Library
          </h2>
          <p className="text-slate-400 mt-1">Manage images and video files</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Media</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          <select 
            value={sortBy} 
            onChange={(e) => { setSortBy(e.target.value); fetchMedia(); }}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Name A-Z</option>
          </select>
          <label className="cursor-pointer">
            <input type="file" className="hidden" accept="image/*,video/*" onChange={handleUpload} disabled={uploading} />
            <Button className="bg-pink-600 hover:bg-pink-700" disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </label>
        </div>
      </div>

      {/* Visual Protection Notice */}
      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-blue-400 font-medium text-sm">Current Cinematic Visuals Protected</p>
          <p className="text-blue-400/70 text-xs mt-1">All existing page visuals remain unchanged. New uploads add to your library.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : media.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Image className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">No Media Files</h3>
            <p className="text-slate-500">Upload images or videos to build your library.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {media.map((item) => (
            <Card 
              key={item.id} 
              className={`bg-slate-800/50 border-slate-700 overflow-hidden group cursor-pointer transition-all ${
                selectedMedia?.id === item.id ? 'ring-2 ring-purple-500' : 'hover:border-slate-600'
              }`}
              onClick={() => setSelectedMedia(selectedMedia?.id === item.id ? null : item)}
            >
              <div className="aspect-video bg-slate-900 relative">
                {item.media_type === 'image' ? (
                  <img 
                    src={`${API_URL}/api/admin/cms/media/${item.id}/download`}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
                    <Image className="w-8 h-8 text-slate-600" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <a href={`${API_URL}/api/admin/cms/media/${item.id}/download`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="text-white hover:bg-white/20">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </a>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/20" onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm text-white truncate">{item.title}</p>
                <p className="text-xs text-slate-500">{item.media_type} · {(item.size / 1024).toFixed(1)} KB</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Selected Media Details Panel */}
      {selectedMedia && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Media Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-400 text-sm">File Name</Label>
                <p className="text-white">{selectedMedia.original_filename}</p>
              </div>
              <div>
                <Label className="text-slate-400 text-sm">Type</Label>
                <p className="text-white capitalize">{selectedMedia.media_type}</p>
              </div>
              <div>
                <Label className="text-slate-400 text-sm">Size</Label>
                <p className="text-white">{(selectedMedia.size / 1024).toFixed(2)} KB</p>
              </div>
              <div>
                <Label className="text-slate-400 text-sm">Uploaded</Label>
                <p className="text-white">{new Date(selectedMedia.created_at).toLocaleString()}</p>
              </div>
            </div>
            <div>
              <Label className="text-slate-400 text-sm">Direct URL (for CMS linking)</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={`${API_URL}/api/admin/cms/public/media/${selectedMedia.id}`}
                  readOnly
                  className="bg-slate-900 border-slate-700 text-slate-400 text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-slate-600"
                  onClick={() => {
                    navigator.clipboard.writeText(`${API_URL}/api/admin/cms/public/media/${selectedMedia.id}`);
                    toast.success('URL copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================
// PHASE 2: DOCUMENTS - ENHANCED UI
// ============================================
function DocumentsSection() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('all');
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', description: '', category: 'general', page_assignment: '' });

  const CATEGORIES = [
    { value: 'general', label: 'General' },
    { value: 'programs', label: 'Programs' },
    { value: 'admissions', label: 'Admissions' },
    { value: 'research', label: 'Research' },
    { value: 'policies', label: 'Policies' },
  ];

  const PAGES = [
    { value: '', label: 'Not Assigned' },
    { value: 'home', label: 'Home' },
    { value: 'programs', label: 'Programs' },
    { value: 'admissions', label: 'Admissions' },
    { value: 'research', label: 'Research' },
    { value: 'about', label: 'About' },
  ];

  useEffect(() => {
    fetchDocuments();
  }, [category]);

  const fetchDocuments = async () => {
    try {
      const url = category === 'all' 
        ? `${API_URL}/api/admin/cms/documents`
        : `${API_URL}/api/admin/cms/documents?category=${category}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      toast.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', uploadData.title || file.name);
    formData.append('description', uploadData.description);
    formData.append('category', uploadData.category);
    formData.append('page_assignment', uploadData.page_assignment);

    try {
      const res = await fetch(`${API_URL}/api/admin/cms/documents/upload`, {
        method: 'POST',
        body: formData
      });
      if (res.ok) {
        toast.success('Document uploaded successfully');
        fetchDocuments();
        setShowUploadForm(false);
        setUploadData({ title: '', description: '', category: 'general', page_assignment: '' });
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/documents/${docId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Document deleted');
        fetchDocuments();
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="documents-section">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FolderOpen className="w-7 h-7 text-cyan-400" />
            Documents
          </h2>
          <p className="text-slate-400 mt-1">Manage downloadable files and resources</p>
        </div>
        <div className="flex gap-2">
          <select 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <Button 
            className="bg-cyan-600 hover:bg-cyan-700"
            onClick={() => setShowUploadForm(!showUploadForm)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Document
          </Button>
        </div>
      </div>

      {/* Upload Form */}
      <AnimatePresence>
        {showUploadForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-lg">Upload New Document</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Title</Label>
                    <Input
                      value={uploadData.title}
                      onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Document title"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Category</Label>
                    <select
                      value={uploadData.category}
                      onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full bg-slate-900 border-slate-700 text-white rounded-lg px-3 py-2"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Description</Label>
                    <Input
                      value={uploadData.description}
                      onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Brief description"
                      className="bg-slate-900 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 mb-2 block">Link to Page</Label>
                    <select
                      value={uploadData.page_assignment}
                      onChange={(e) => setUploadData(prev => ({ ...prev, page_assignment: e.target.value }))}
                      className="w-full bg-slate-900 border-slate-700 text-white rounded-lg px-3 py-2"
                    >
                      {PAGES.map(page => (
                        <option key={page.value} value={page.value}>{page.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <label className="cursor-pointer flex-1">
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.csv" onChange={handleUpload} disabled={uploading} />
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700" disabled={uploading}>
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Select & Upload File
                    </Button>
                  </label>
                  <Button variant="outline" onClick={() => setShowUploadForm(false)} className="border-slate-600 text-slate-300">
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : documents.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">No Documents</h3>
            <p className="text-slate-500">Upload PDFs or documents to make them available for download.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => (
            <Card key={doc.id} className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{doc.title}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-500">{doc.file_type?.toUpperCase()}</span>
                        <span className="text-xs text-slate-600">·</span>
                        <span className="text-xs text-slate-500">{(doc.size / 1024).toFixed(1)} KB</span>
                        <span className="text-xs text-slate-600">·</span>
                        <span className="text-xs text-slate-500">{doc.download_count || 0} downloads</span>
                        {doc.page_assignment && (
                          <>
                            <span className="text-xs text-slate-600">·</span>
                            <span className="text-xs text-cyan-400 flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              {doc.page_assignment}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      doc.category === 'programs' ? 'bg-purple-500/20 text-purple-400' :
                      doc.category === 'admissions' ? 'bg-blue-500/20 text-blue-400' :
                      doc.category === 'research' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-slate-600 text-slate-400'
                    }`}>
                      {doc.category}
                    </span>
                    <a href={`${API_URL}/api/admin/cms/documents/${doc.id}/download`} download>
                      <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white">
                        <Download className="w-4 h-4" />
                      </Button>
                    </a>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/20" onClick={() => deleteDocument(doc.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {doc.description && (
                  <p className="text-sm text-slate-500 mt-2 ml-16">{doc.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Public Access Info */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium text-sm">Public Download URLs</p>
              <p className="text-slate-500 text-xs mt-1">
                Documents are publicly accessible at: <code className="bg-slate-900 px-1 rounded">/api/admin/cms/public/documents/[id]/download</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================
// USER MANAGEMENT (Keep existing logic)
// ============================================
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [applications, setApplications] = useState({ pending_instructors: [], program_applications: [] });

  // Invite User modal state (Operational Launch v1)
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'student', is_active: true });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { user_id, email, role, invitation_email }

  useEffect(() => {
    fetchUsers();
    fetchApplications();
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const url = filter === 'all' 
        ? `${API_URL}/api/admin/cms/users`
        : `${API_URL}/api/admin/cms/users?role=${filter}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/users/applications/pending`);
      if (res.ok) {
        const data = await res.json();
        setApplications(data);
      }
    } catch (err) {
      console.error('Failed to fetch applications');
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ portal_role: newRole })
      });
      if (res.ok) {
        toast.success('User role updated');
        fetchUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Update failed');
      }
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const approveApplication = async (appId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/users/applications/${appId}/approve`, { method: 'POST' });
      if (res.ok) {
        toast.success('Application approved');
        fetchApplications();
        fetchUsers();
      }
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const rejectApplication = async (appId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/cms/users/applications/${appId}/reject`, { method: 'POST' });
      if (res.ok) {
        toast.success('Application rejected');
        fetchApplications();
      }
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  // Invite User (Operational Launch v1) — calls /api/portal/admin/invite, role-gated
  const submitInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setInviteLoading(true);
    setInviteResult(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/portal/admin/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          name: inviteForm.name.trim(),
          email: inviteForm.email.trim().toLowerCase(),
          role: inviteForm.role,
          is_active: inviteForm.is_active,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Failed to invite user');
        return;
      }
      setInviteResult(data);
      const emailStatus = data?.invitation_email?.status;
      if (emailStatus === 'sent') {
        toast.success(`Invitation sent to ${data.email}`);
      } else if (emailStatus === 'skipped') {
        toast.warning('User created but email delivery was skipped (RESEND_API_KEY missing)');
      } else {
        toast.warning('User created but email delivery failed — check logs');
      }
      fetchUsers();
    } catch (err) {
      toast.error('Failed to invite user');
    } finally {
      setInviteLoading(false);
    }
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteForm({ name: '', email: '', role: 'student', is_active: true });
    setInviteResult(null);
  };

  const roleColors = {
    admin: 'bg-amber-500/20 text-amber-400',
    instructor: 'bg-purple-500/20 text-purple-400',
    student: 'bg-blue-500/20 text-blue-400'
  };

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-purple-400" />
            User Management
          </h2>
          <p className="text-slate-400 mt-1">Manage accounts and permissions</p>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-700 border-slate-600 text-white rounded-lg px-3 py-2"
            data-testid="user-management-filter"
          >
            <option value="all">All Users</option>
            <option value="admin">Admins</option>
            <option value="instructor">Instructors</option>
            <option value="student">Students</option>
          </select>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="invite-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Pending Applications */}
      {applications.program_applications?.length > 0 && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardHeader>
            <CardTitle className="text-amber-400 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Pending Applications ({applications.program_applications.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {applications.program_applications.map((app) => (
                <div key={app.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{app.name}</p>
                    <p className="text-sm text-slate-400">{app.email} · {app.program}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approveApplication(app.id)}>
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/20" onClick={() => rejectApplication(app.id)}>
                      <XCircle className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        </div>
      ) : users.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">No Users Found</h3>
            <p className="text-slate-500">No users match the selected filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map((user) => (
            <Card key={user.id} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${roleColors[user.portal_role] || 'bg-slate-600 text-slate-400'}`}>
                      {user.portal_role === 'admin' ? <Shield className="w-5 h-5" /> :
                       user.portal_role === 'instructor' ? <UserCog className="w-5 h-5" /> :
                       <GraduationCap className="w-5 h-5" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.name || user.email}</p>
                      <p className="text-sm text-slate-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${roleColors[user.portal_role] || 'bg-slate-600 text-slate-400'}`}>
                      {user.portal_role}
                    </span>
                    <select
                      value={user.portal_role}
                      onChange={(e) => updateUserRole(user.id, e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white rounded px-2 py-1 text-sm"
                    >
                      <option value="student">Student</option>
                      <option value="instructor">Instructor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Invite User Modal — Operational Launch v1 */}
      {showInviteModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeInviteModal}
          data-testid="invite-user-modal"
        >
          <div
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-purple-400" />
                  Invite User
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  Create an institutional account and send a branded invitation email.
                </p>
              </div>
              <button
                onClick={closeInviteModal}
                className="text-slate-400 hover:text-white"
                data-testid="invite-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!inviteResult ? (
              <div className="p-6 space-y-4">
                <div>
                  <Label htmlFor="invite-name" className="text-slate-300">Full Name</Label>
                  <Input
                    id="invite-name"
                    type="text"
                    value={inviteForm.name}
                    onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white mt-1"
                    placeholder="e.g. Jane Smith"
                    data-testid="invite-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="invite-email" className="text-slate-300">Email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white mt-1"
                    placeholder="jane@example.org"
                    data-testid="invite-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="invite-role" className="text-slate-300">Role</Label>
                  <select
                    id="invite-role"
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-600 text-white rounded-lg px-3 py-2 mt-1"
                    data-testid="invite-role-select"
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Administrator</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {inviteForm.role === 'admin'
                      ? 'Admins log in privately at /internal/admin.'
                      : 'Students and Instructors log in at /login.'}
                  </p>
                </div>

                <div className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                  <div>
                    <p className="text-sm text-slate-300 font-medium">Active</p>
                    <p className="text-xs text-slate-500">Account can sign in immediately.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInviteForm({ ...inviteForm, is_active: !inviteForm.is_active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      inviteForm.is_active ? 'bg-purple-600' : 'bg-slate-600'
                    }`}
                    data-testid="invite-active-toggle"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        inviteForm.is_active ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={closeInviteModal}
                    className="flex-1 bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                    disabled={inviteLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitInvite}
                    disabled={inviteLoading}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="invite-submit-btn"
                  >
                    {inviteLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Create &amp; Send Invite
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4" data-testid="invite-result-panel">
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4">
                  <div className="flex items-center gap-2 text-emerald-400 font-semibold mb-1">
                    <CheckCircle2 className="w-5 h-5" />
                    User Created
                  </div>
                  <p className="text-sm text-slate-300">
                    <strong>{inviteResult.name}</strong> ({inviteResult.email}) was added as <strong>{inviteResult.role}</strong>.
                  </p>
                </div>
                <div
                  className={`rounded-lg p-4 border ${
                    inviteResult.invitation_email?.status === 'sent'
                      ? 'bg-blue-500/10 border-blue-500/30'
                      : 'bg-amber-500/10 border-amber-500/30'
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-200 mb-1">Invitation email</div>
                  <div className="text-xs text-slate-400">
                    Status: <strong>{inviteResult.invitation_email?.status || 'unknown'}</strong>
                    {inviteResult.invitation_email?.email_id && (
                      <span> · Resend id: <code className="text-slate-300">{inviteResult.invitation_email.email_id}</code></span>
                    )}
                    {inviteResult.invitation_email?.reason && (
                      <span> · {inviteResult.invitation_email.reason}</span>
                    )}
                  </div>
                </div>
                <Button
                  onClick={closeInviteModal}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="invite-done-btn"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// CERTIFICATE MANAGEMENT SECTION
// ============================================
function CertificateManagement() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // New certificate form
  const [newCert, setNewCert] = useState({
    holder_name: '',
    holder_email: '',
    certificate_type: 'WIP',
    program_title: '',
    completion_date: ''
  });

  useEffect(() => {
    fetchCertificates();
  }, [statusFilter, typeFilter]);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/certificates/list?limit=100`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (typeFilter !== 'all') url += `&certificate_type=${typeFilter}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setCertificates(data.certificates || []);
      }
    } catch (err) {
      toast.error('Failed to fetch certificates');
    } finally {
      setLoading(false);
    }
  };

  const createCertificate = async () => {
    if (!newCert.holder_name || !newCert.holder_email || !newCert.program_title || !newCert.completion_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      setActionLoading(true);
      const res = await fetch(`${API_URL}/api/certificates/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCert)
      });
      
      if (res.ok) {
        toast.success('Certificate created successfully');
        setShowCreateModal(false);
        setNewCert({ holder_name: '', holder_email: '', certificate_type: 'WIP', program_title: '', completion_date: '' });
        fetchCertificates();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to create certificate');
      }
    } catch (err) {
      toast.error('Failed to create certificate');
    } finally {
      setActionLoading(false);
    }
  };

  const revokeCertificate = async (certId) => {
    try {
      setActionLoading(true);
      const res = await fetch(`${API_URL}/api/certificates/${certId}/revoke?reason=${encodeURIComponent(revokeReason)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Certificate revoked');
        setShowRevokeModal(null);
        setRevokeReason('');
        fetchCertificates();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to revoke');
      }
    } catch (err) {
      toast.error('Failed to revoke certificate');
    } finally {
      setActionLoading(false);
    }
  };

  const reinstateCertificate = async (certId) => {
    try {
      setActionLoading(true);
      const res = await fetch(`${API_URL}/api/certificates/${certId}/reinstate`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success('Certificate reinstated');
        fetchCertificates();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to reinstate');
      }
    } catch (err) {
      toast.error('Failed to reinstate certificate');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadPDF = async (certId, holderName) => {
    try {
      const res = await fetch(`${API_URL}/api/certificates/${certId}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Zodiac_Certificate_${holderName.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Cannot download PDF');
      }
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const openVerification = (certId) => {
    window.open(`/verify-certificate/${certId}`, '_blank');
  };

  // Filter certificates by search
  const filteredCertificates = certificates.filter(cert => {
    const query = searchQuery.toLowerCase();
    return (
      cert.holder_name?.toLowerCase().includes(query) ||
      cert.holder_email?.toLowerCase().includes(query) ||
      cert.certificate_number?.toLowerCase().includes(query) ||
      cert.program_title?.toLowerCase().includes(query) ||
      cert.id?.toLowerCase().includes(query)
    );
  });

  const statusColors = {
    valid: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    revoked: 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const typeLabels = {
    WIP: 'Workforce Intelligence Professional',
    AI_SPECIALIST: 'AI Workforce Integration Specialist',
    RESILIENCE: 'Organizational Resilience Practitioner',
    QUALITY_SAFETY: 'Quality & Safety Management'
  };

  const certTypeColors = {
    WIP: 'bg-blue-500/20 text-blue-400',
    AI_SPECIALIST: 'bg-purple-500/20 text-purple-400',
    RESILIENCE: 'bg-amber-500/20 text-amber-400',
    QUALITY_SAFETY: 'bg-teal-500/20 text-teal-400'
  };

  return (
    <div className="space-y-6" data-testid="certificate-management">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Award className="w-7 h-7 text-amber-400" />
            Certificate Management
          </h2>
          <p className="text-slate-400 mt-1">Issue, view, and manage training certificates</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
          data-testid="create-certificate-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Issue Certificate
        </Button>
      </div>

      {/* Filters & Search */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search by name, email, ID, or program..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
            data-testid="certificate-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-slate-800/50 border border-slate-700 text-white rounded-lg px-3 py-2"
          data-testid="status-filter"
        >
          <option value="all">All Status</option>
          <option value="valid">Valid</option>
          <option value="revoked">Revoked</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-slate-800/50 border border-slate-700 text-white rounded-lg px-3 py-2"
          data-testid="type-filter"
        >
          <option value="all">All Types</option>
          <option value="WIP">WIP</option>
          <option value="AI_SPECIALIST">AI Specialist</option>
          <option value="RESILIENCE">Resilience</option>
          <option value="QUALITY_SAFETY">Quality & Safety</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{certificates.length}</p>
                <p className="text-xs text-slate-400">Total Certificates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{certificates.filter(c => c.status === 'valid').length}</p>
                <p className="text-xs text-slate-400">Active/Valid</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{certificates.filter(c => c.status === 'revoked').length}</p>
                <p className="text-xs text-slate-400">Revoked</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <QrCode className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{filteredCertificates.length}</p>
                <p className="text-xs text-slate-400">Matching Search</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificates Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
        </div>
      ) : filteredCertificates.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Award className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-slate-400 mb-2">No Certificates Found</h3>
            <p className="text-slate-500">
              {searchQuery ? 'No certificates match your search criteria.' : 'No certificates have been issued yet.'}
            </p>
            {!searchQuery && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="mt-4 bg-amber-500 hover:bg-amber-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Issue First Certificate
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-slate-800/50 border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="certificates-table">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Certificate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Holder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Program</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Issued</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {filteredCertificates.map((cert) => (
                  <tr key={cert.id} className="hover:bg-slate-700/30 transition-colors" data-testid={`cert-row-${cert.id}`}>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-white font-mono text-sm">{cert.certificate_number}</p>
                        <p className="text-xs text-slate-500">{cert.id?.slice(0, 8)}...</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-white font-medium">{cert.holder_name}</p>
                        <p className="text-sm text-slate-400">{cert.holder_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${certTypeColors[cert.certificate_type] || 'bg-slate-600/50 text-slate-300'}`}>
                        {cert.certificate_type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium border ${statusColors[cert.status] || 'bg-slate-600 text-slate-300'}`}>
                        {cert.status === 'valid' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Ban className="w-3 h-3 mr-1" />}
                        {cert.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-400">
                      {cert.issue_date ? new Date(cert.issue_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {/* View Details */}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-slate-400 hover:text-white hover:bg-slate-700"
                          onClick={() => setShowDetailsModal(cert)}
                          data-testid={`view-details-${cert.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {/* Open Verification Page */}
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          onClick={() => openVerification(cert.id)}
                          data-testid={`verify-${cert.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        
                        {/* Download PDF (only valid) */}
                        {cert.status === 'valid' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => downloadPDF(cert.id, cert.holder_name)}
                            data-testid={`download-pdf-${cert.id}`}
                          >
                            <FileDown className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Revoke / Reinstate */}
                        {cert.status === 'valid' ? (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setShowRevokeModal(cert)}
                            data-testid={`revoke-${cert.id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            onClick={() => reinstateCertificate(cert.id)}
                            disabled={actionLoading}
                            data-testid={`reinstate-${cert.id}`}
                          >
                            <RotateCw className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Create Certificate Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-lg" data-testid="create-certificate-modal">
            <CardHeader className="border-b border-slate-700">
              <CardTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-amber-400" />
                Issue New Certificate
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Holder Name *</label>
                <Input
                  value={newCert.holder_name}
                  onChange={(e) => setNewCert({...newCert, holder_name: e.target.value})}
                  placeholder="John Smith"
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="holder-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Holder Email *</label>
                <Input
                  type="email"
                  value={newCert.holder_email}
                  onChange={(e) => setNewCert({...newCert, holder_email: e.target.value})}
                  placeholder="john@example.com"
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="holder-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Certificate Type *</label>
                <select
                  value={newCert.certificate_type}
                  onChange={(e) => setNewCert({...newCert, certificate_type: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 text-white rounded-lg px-3 py-2"
                  data-testid="cert-type-select"
                >
                  <option value="WIP">Workforce Intelligence Professional (WIP)</option>
                  <option value="AI_SPECIALIST">AI Workforce Integration Specialist</option>
                  <option value="RESILIENCE">Organizational Resilience Practitioner</option>
                  <option value="QUALITY_SAFETY">Quality & Safety Management</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Program Title *</label>
                <Input
                  value={newCert.program_title}
                  onChange={(e) => setNewCert({...newCert, program_title: e.target.value})}
                  placeholder="Workforce Intelligence Professional (WIP)"
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="program-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Completion Date *</label>
                <Input
                  type="date"
                  value={newCert.completion_date}
                  onChange={(e) => setNewCert({...newCert, completion_date: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="completion-date-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="ghost" 
                  className="flex-1 text-slate-400 hover:bg-slate-700"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                  onClick={createCertificate}
                  disabled={actionLoading}
                  data-testid="submit-certificate-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Issue Certificate
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Certificate Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-lg" data-testid="certificate-details-modal">
            <CardHeader className="border-b border-slate-700">
              <CardTitle className="text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" />
                Certificate Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Certificate Number</p>
                  <p className="text-white font-mono">{showDetailsModal.certificate_number}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  <span className={`inline-flex px-2 py-1 rounded-md text-xs font-medium ${statusColors[showDetailsModal.status]}`}>
                    {showDetailsModal.status?.toUpperCase()}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Holder Name</p>
                <p className="text-white font-medium">{showDetailsModal.holder_name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Holder Email</p>
                <p className="text-white">{showDetailsModal.holder_email}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Program</p>
                <p className="text-white">{showDetailsModal.program_title}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Completion Date</p>
                  <p className="text-white">{showDetailsModal.completion_date}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Issue Date</p>
                  <p className="text-white">{showDetailsModal.issue_date ? new Date(showDetailsModal.issue_date).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              {showDetailsModal.status === 'revoked' && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-xs text-red-400 uppercase mb-1">Revocation Reason</p>
                  <p className="text-red-300">{showDetailsModal.revocation_reason || 'Not specified'}</p>
                  <p className="text-xs text-red-400/70 mt-2">
                    Revoked on: {showDetailsModal.revoked_at ? new Date(showDetailsModal.revoked_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="ghost" 
                  className="flex-1 text-slate-400 hover:bg-slate-700"
                  onClick={() => setShowDetailsModal(null)}
                >
                  Close
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={() => openVerification(showDetailsModal.id)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Verification
                </Button>
                {showDetailsModal.status === 'valid' && (
                  <Button 
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                    onClick={() => downloadPDF(showDetailsModal.id, showDetailsModal.holder_name)}
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Revoke Certificate Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-slate-800 border-slate-700 w-full max-w-md" data-testid="revoke-certificate-modal">
            <CardHeader className="border-b border-slate-700">
              <CardTitle className="text-red-400 flex items-center gap-2">
                <Ban className="w-5 h-5" />
                Revoke Certificate
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-300 text-sm">
                  You are about to revoke the certificate for <strong>{showRevokeModal.holder_name}</strong>.
                  This action can be reversed by reinstating the certificate.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Revocation Reason</label>
                <Input
                  value={revokeReason}
                  onChange={(e) => setRevokeReason(e.target.value)}
                  placeholder="Enter reason for revocation..."
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="revoke-reason-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="ghost" 
                  className="flex-1 text-slate-400 hover:bg-slate-700"
                  onClick={() => { setShowRevokeModal(null); setRevokeReason(''); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={() => revokeCertificate(showRevokeModal.id)}
                  disabled={actionLoading}
                  data-testid="confirm-revoke-btn"
                >
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                  Revoke Certificate
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN ADMIN DASHBOARD
// ============================================
const sectionComponents = {
  dashboard: DashboardHome,
  applications: ApplicationsInbox,
  content: ContentManagement,
  media: MediaLibrary,
  documents: DocumentsSection,
  certificates: CertificateManagement,
  users: UserManagement,
};

export default function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/internal/admin', { replace: true });
      return;
    }
    
    if (user && user.portal_role !== 'admin') {
      if (user.portal_role === 'student') {
        navigate('/student/dashboard', { replace: true });
      } else if (user.portal_role === 'instructor') {
        navigate('/instructor/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/internal/admin', { replace: true });
  };

  const handleNavigate = useCallback((section) => {
    setActiveSection(section);
    setMobileMenuOpen(false);
  }, []);

  const ActiveSectionComponent = sectionComponents[activeSection] || DashboardHome;

  if (!isAuthenticated || !user || user.portal_role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="admin-dashboard">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-800 border-r border-slate-700 transition-all duration-300`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          {sidebarOpen && (
            <div className="flex items-center gap-3">
              <img
                src={OFFICIAL_SEAL}
                alt="Zodiac Institute"
                className="w-10 h-10 object-contain drop-shadow-[0_2px_6px_rgba(200,161,87,0.35)]"
                data-testid="admin-sidebar-seal"
              />
              <div className="leading-tight">
                <div className="font-bold text-white text-sm">Zodiac</div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Admin</div>
              </div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-slate-700 text-slate-400">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => (
            item.external ? (
              <a
                key={item.id}
                href={item.external}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-slate-400 hover:bg-slate-700 hover:text-white`}
                title={!sidebarOpen ? item.label : undefined}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="text-sm font-medium flex items-center gap-2">
                    {item.label}
                    <ExternalLink className="w-3 h-3" />
                  </span>
                )}
              </a>
            ) : (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  activeSection === item.id 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/20' 
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                }`}
                title={!sidebarOpen ? item.label : undefined}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </button>
            )
          ))}
        </nav>

        <div className="p-4 border-t border-slate-700">
          {sidebarOpen && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-semibold">{user?.email?.charAt(0).toUpperCase() || 'A'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">Administrator</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
            title={!sidebarOpen ? 'Logout' : undefined}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)} />}

      {/* Mobile Sidebar */}
      <aside className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-slate-800 border-r border-slate-700 z-50 transform transition-transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <img
              src={OFFICIAL_SEAL}
              alt="Zodiac Institute"
              className="w-10 h-10 object-contain drop-shadow-[0_2px_6px_rgba(200,161,87,0.35)]"
            />
            <div className="leading-tight">
              <div className="font-bold text-white text-sm">Zodiac</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Admin</div>
            </div>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-lg hover:bg-slate-700 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => (
            item.external ? (
              <a
                key={item.id}
                href={item.external}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors text-slate-400 hover:bg-slate-700 hover:text-white`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium flex items-center gap-2">
                  {item.label}
                  <ExternalLink className="w-3 h-3" />
                </span>
              </a>
            ) : (
              <button
                key={item.id}
                onClick={() => handleNavigate(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                  activeSection === item.id 
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white' 
                    : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            )
          ))}
        </nav>
        <div className="p-4 border-t border-slate-700">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-colors">
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-700 text-slate-400">
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-400">
              <Home className="w-4 h-4" />
              <ChevronRight className="w-4 h-4" />
              <span className="text-white font-medium">{navItems.find(i => i.id === activeSection)?.label}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:block relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input placeholder="Search..." className="w-64 pl-9 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500" />
            </div>
          </div>
        </header>

        <div className="flex-1 p-4 lg:p-6 overflow-auto">
          <motion.div key={activeSection} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <ActiveSectionComponent onNavigate={handleNavigate} />
          </motion.div>
        </div>
      </main>
    </div>
  );
}
